import { getVapidKey, postDeviceState, subscribePush, unsubscribePush, updateSettings } from './api.js';

export interface PushState {
  /** Browser kann Web-Push (SW + PushManager + secure context). */
  supported: boolean;
  ios: boolean;
  /** Als PWA am Home-Bildschirm installiert (auf iOS Pflicht für Push). */
  standalone: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
}

function isStandalone(): boolean {
  const mm =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone =
    (window.navigator as unknown as { standalone?: boolean }).standalone === true;
  return mm || iosStandalone;
}

/**
 * iPadOS ≥ 13 meldet in Safari standardmäßig einen Desktop-User-Agent ('Macintosh',
 * kein 'ipad'). Einzige zuverlässige Unterscheidung: Touch-Punkte. Damit ein iPad nicht
 * als „Mac" durchgeht (Push braucht dort die Home-Screen-Installation).
 */
export function isIpadOS(): boolean {
  return (
    /macintosh/i.test(navigator.userAgent || '') &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1
  );
}

/** Aktueller Push-Zustand des Geräts — für die progressiven Onboarding-Hinweise. */
export async function getPushState(): Promise<PushState> {
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent || '') || isIpadOS();
  const supported = pushSupported();
  const permission: PushState['permission'] =
    typeof Notification !== 'undefined' ? Notification.permission : 'unsupported';
  let subscribed = false;
  if (supported) {
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      subscribed = (await reg?.pushManager.getSubscription()) != null;
    } catch {
      subscribed = false;
    }
  }
  return { supported, ios, standalone: isStandalone(), permission, subscribed };
}

const DEVICE_ID_KEY = 'bg_device_id';
/** Prozess-stabile Ersatz-ID falls localStorage nicht verfügbar (nicht persistent,
 *  aber wenigstens keine neue Zeile pro Aufruf → keine Phantom-Geräte). */
let ephemeralDeviceId: string | null = null;

/** Stabile Geräte-/Install-Kennung (localStorage, pro Install eindeutig). */
function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage gesperrt (Private Mode etc.) → prozess-stabile Ersatz-ID einmalig mint.
    ephemeralDeviceId ??= `eph-${Math.random().toString(36).slice(2, 12)}`;
    return ephemeralDeviceId;
  }
}

/**
 * Meldet den aktuellen Geräte-/Push-Zustand an den Server (best effort, wirft nie).
 * Damit sieht der Betreiber, wie weit welche Person auf welchem Gerät bei
 * PWA-Installation + Benachrichtigungen ist. Wird beim App-Start aufgerufen.
 */
export async function reportDeviceState(): Promise<void> {
  try {
    const s = await getPushState();
    await postDeviceState({
      deviceId: getDeviceId(),
      standalone: s.standalone,
      permission: s.permission,
      subscribed: s.subscribed,
      supported: s.supported,
      ios: s.ios,
      // iPad tarnt sich als Mac im UA → Client-Hinweis, damit das Label stimmt.
      platformHint: isIpadOS() ? 'iPad' : undefined,
    });
  } catch {
    // Reporting ist optional — Fehler (offline, 401, Impersonation-Skip) still schlucken.
  }
}

/**
 * Web-Push-Aktivierung im Browser. Funktioniert NUR im secure context
 * (HTTPS oder localhost) — auf http://LAN-IP registriert der Browser keinen
 * Service Worker. Die UI ruft `pushSupported()` vorher ab und zeigt sonst einen Hinweis.
 */

export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    window.isSecureContext
  );
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  // Expliziter ArrayBuffer (nicht ArrayBufferLike), damit der Typ zu BufferSource passt.
  const arr = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function getRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration();
  return existing ?? (await navigator.serviceWorker.register('/sw.js'));
}

/** Aktiviert Web-Push: SW registrieren, Permission holen, subscriben, an Server melden. */
export async function enablePush(): Promise<void> {
  if (!pushSupported()) throw new Error('Web-Push wird hier nicht unterstützt (kein HTTPS?)');
  const { publicKey } = await getVapidKey();
  if (!publicKey) throw new Error('Server-seitig ist Web-Push nicht konfiguriert');

  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Benachrichtigungen wurden nicht erlaubt');

  const reg = await getRegistration();
  await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
  });
  await subscribePush(sub.toJSON() as PushSubscriptionJSON);
  // Kanal-Schalter setzen, sonst würde der Push-Dispatch das Gerät überspringen.
  await updateSettings({ webpushNotify: true });
  // Frischen Zustand sofort ans Admin-Tracking melden (sonst bis zum nächsten Reload „aus").
  await reportDeviceState();
}

/** Deaktiviert Web-Push auf diesem Gerät. */
export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await unsubscribePush(sub.endpoint);
    await sub.unsubscribe();
  }
  await reportDeviceState();
}
